// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerRelationshipDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action, toJS } from 'mobx';
import {
  OBJECT_STATE,
  DEFAULT_COMPARISION_OPERATOR,
  EMPTY_GUID,
  JOIN_TYPE_CROSS
} from 'logic/data/Izenda.ReportDesignerConstants';
import ReportDesignerKeyJoinDetail from './Izenda.ReportDesignerKeyJoinDetail';
import tracker from 'share/tracking/Izenda.Tracker';
import {
  filter,
  isEmpty,
  get,
  uniqueId,
  getOr,
  isNil,
  some,
  forEach,
  includes,
  find,
  findIndex,
  cloneDeep,
  placeholder
} from 'lodash/fp';

@tracker
class ReportDesignerRelationshipDetail {
  @observable joinConnectionId;
  @observable foreignConnectionId;
  @observable joinQuerySourceId;
  @observable joinQuerySourceName;
  @observable joinDataSourceCategoryName;
  @observable joinDataSourceCategoryId;
  @observable foreignDataSourceCategoryName;
  @observable foreignDataSourceCategoryId;
  @observable foreignQuerySourceId;
  @observable foreignQuerySourceName;
  @observable joinFieldId;
  @observable joinFieldName;
  @observable foreignFieldId;
  @observable foreignFieldName;
  @observable alias;
  @observable aliasTempId;
  @observable systemRelationship;
  @observable joinType;
  @observable parentRelationshipId;
  @observable position;
  @observable relationshipKeyJoins;
  @observable reportId;
  @observable selectedForeignAlias;
  // This flag is use to mark the foreign data object of this relationship use alias
  @observable isForeignDataObjectAlias;
  @observable id;
  @observable state;
  @observable validationKey;
  @observable relationshipPosition;
  @observable needAlias;
  @observable level;
  @observable comparisonOperator;
  @observable comparisonValue;
  @observable hidden = false;
  @observable invalidAlias;

  /**
   * Create new relationship detail
   * @param {any} detail
   */
  constructor(detail: any) {
    this.previousAlias = '';
    this.tempId = '';
    this.hasBeenModified = false;

    this.populateData(detail || {});
    this.cleanDirty();
  }

  @computed
  get displayKeyJoins() {
    return filter({ deleted: false }, this.relationshipKeyJoins);
  }

  @computed
  get deleted() {
    return this.state === OBJECT_STATE.DELETE;
  }

  @computed
  get isJoinQuerySourceReady() {
    return !isEmpty(this.joinDataSourceCategoryId);
  }

  @computed
  get isforeignQuerySourceReady() {
    return !isEmpty(this.foreignDataSourceCategoryId);
  }

  @computed
  get isJoinFieldReady() {
    return !isEmpty(this.joinQuerySourceId);
  }

  @computed
  get isForeignFieldReady() {
    return !isEmpty(this.selectedForeignAlias);
  }

  @computed
  get keyJoinCount() {
    const availableRelationships = filter(
      relationship => relationship.state !== OBJECT_STATE.DELETE,
      this.relationshipKeyJoins.slice()
    );
    return availableRelationships.length;
  }

  @computed
  get originalAliasId() {
    if (this.selectedForeignAlias && this.selectedForeignAlias.indexOf('_') >= 0) {
      const underscoreIndex = this.selectedForeignAlias.indexOf('_');
      return this.selectedForeignAlias.substring(0, underscoreIndex);
    }
    return this.selectedForeignAlias;
  }

  /**
   * Populate data for relationship
   * (This is intended to populating which is returned from API.)
   * @param {any} detail
   */
  @action('populateData')
  populateData(detail: any) {
    this.tempId = get('id', detail) || uniqueId('relationship_');
    this.joinConnectionId = getOr(null, 'joinConnectionId', detail);
    this.foreignConnectionId = getOr(null, 'foreignConnectionId', detail);
    this.joinQuerySourceId = getOr(null, 'joinQuerySourceId', detail);
    this.joinQuerySourceName = get('joinQuerySourceName', detail) || '';
    this.joinDataSourceCategoryName = get('joinDataSourceCategoryName', detail) || '';
    this.joinDataSourceCategoryId = getOr(null, 'joinDataSourceCategoryId', detail);
    this.foreignDataSourceCategoryName = get('foreignDataSourceCategoryName', detail) || '';
    this.foreignDataSourceCategoryId = getOr(null, 'foreignDataSourceCategoryId', detail);
    this.foreignQuerySourceId = getOr(null, 'foreignQuerySourceId', detail);
    this.foreignQuerySourceName = get('foreignQuerySourceName', detail) || '';
    this.joinFieldId = getOr(null, 'joinFieldId', detail);
    this.joinFieldName = get('joinFieldName', detail) || '';
    this.foreignFieldId = getOr(null, 'foreignFieldId', detail);
    this.foreignFieldName = get('foreignFieldName', detail) || '';
    this.alias = get('alias', detail) || '';
    this.aliasTempId = get('aliasTempId', detail) || uniqueId('alias_');
    this.systemRelationship = get('systemRelationship', detail) || false;
    this.joinType = get('joinType', detail) || 'Inner';
    this.parentRelationshipId = getOr(null, 'parentRelationshipId', detail);
    this.position = getOr(null, 'position', detail);
    this.reportId = getOr(null, 'reportId', detail);
    this.selectedForeignAlias = get('selectedForeignAlias', detail) || '';
    // This flag is use to mark the foreign data object of this relationship use alias
    this.isForeignDataObjectAlias = getOr(null, 'isForeignDataObjectAlias', detail);
    this.id = getOr(null, 'id', detail);
    this.state = !isNil(detail.state) ? detail.state : OBJECT_STATE.INSERT;
    this.validationKey = get('id', detail) || this.tempId;
    this.relationshipPosition = get('relationshipPosition', detail) || 0;
    this.needAlias = !!detail.alias;
    this.previousAlias = get('alias', detail) || '';
    this.level = getOr(null, 'level', detail);
    this.comparisonOperator = get('comparisonOperator', detail) || DEFAULT_COMPARISION_OPERATOR;
    this.comparisonValue = getOr(null, 'comparisonValue', detail);

    this.populateRelationshipKeyJoins(get('relationshipKeyJoins', detail) || []);
    this.setModify(!!detail.hasBeenModified);
  }

  /**
   * Populate key joins.
   * @param {array} keyJoins
   */
  populateRelationshipKeyJoins(keyJoins: Array<any>) {
    this.relationshipKeyJoins = [];

    for (let i = 0; i < keyJoins.length; i++) {
      this.relationshipKeyJoins.push(new ReportDesignerKeyJoinDetail(keyJoins[i]));
    }
  }

  /**
   * Check Dirty
   */
  checkDirty() {
    return this.isDirty || some(['isDirty', true], this.relationshipKeyJoins);
  }

  /**
   * Clean Dirty
   */
  @action('cleanDirty')
  cleanDirty() {
    this.resetDirty();

    forEach(keyJoin => {
      keyJoin.resetDirty();
    }, this.relationshipKeyJoins);
  }

  /**
   * Update join data source category.
   * @param  {string} id
   * @param  {string} name
   */
  @action('setJoinDataSourceCategory')
  setJoinDataSourceCategory(id: string, name: string) {
    this.joinDataSourceCategoryId = id;
    this.joinDataSourceCategoryName = name;
    this.setJoinQuerySource(null);
    this.updateState();
  }

  /**
   * Reset key join category
   * @param {array} categoryIds
   */
  @action('resetKeyJoinCategory')
  resetKeyJoinCategory(categoryIds: Array<any>) {
    if (this.relationshipKeyJoins.length > 0) {
      this.relationshipKeyJoins.forEach(keyjoin => {
        !includes(keyjoin.foreignDataSourceCategoryId, categoryIds) && keyjoin.setForeignDataSourceCategory(null);
        !includes(keyjoin.joinDataSourceCategoryId, categoryIds) && keyjoin.setJoinDataSourceCategory(null);
      });
    }
  }

  /**
   * Reset key join data object
   */
  @action('resetKeyJoinDataObject')
  resetKeyJoinDataObject() {
    if (this.displayKeyJoins.length > 0) {
      this.displayKeyJoins.forEach(keyjoin => {
        keyjoin.setJoinQuerySource(null, null);
        keyjoin.setForeignQuerySource(null, null);
      });
    }
  }

  /**
   * Update foreign data source category
   * @param  {string} id
   */
  @action('setForeignDataSourceCategory')
  setForeignDataSourceCategory(id: string) {
    this.foreignDataSourceCategoryId = id;
    this.setForeignQuerySource(null);
    this.markDirty();
  }

  /**
   * Update join query source.
   * @param  {string} id
   * @param  {string} name
   */
  @action('setJoinQuerySource')
  setJoinQuerySource(id: string, name: string) {
    this.joinQuerySourceId = id;
    this.joinQuerySourceName = name;
    this.joinFieldId = null;
    this.resetKeyJoinDataObject();
  }

  /**
   * Update foreign query source
   * @param {string} id
   * @param {string} name
   * @param {boolean} isDataSourceAlias
   */
  @action('setForeignQuerySource')
  setForeignQuerySource(id: string, name: string, isDataSourceAlias: boolean) {
    this.isForeignDataObjectAlias = !!isDataSourceAlias;
    this.selectedForeignAlias = id;
    this.foreignQuerySourceName = name;
    this.foreignFieldId = null;
    this.resetKeyJoinDataObject();
  }

  /**
   * Set primary for join field
   * @param  {string} id
   * @param  {string} name
   */
  @action('setJoinField')
  setJoinField(id: string, name: string) {
    this.joinFieldId = id;
    this.joinFieldName = name;
    this.updateState();
  }

  /**
   * Set primary for foreign field
   * @param  {string} id
   * @param  {string} name
   */
  @action('setForeignField')
  setForeignField(id: string, name: string) {
    this.foreignFieldId = id;
    this.foreignFieldName = name;
    this.updateState();
  }

  /**
   * Update default value by query source
   * In case user select only one dataObject then we will set default value for relationship base on dataSource selected
   * @param  {any} selectedItem
   * @param  {any} dataTree
   */
  @action('setDefaultValueByQuerySource')
  setDefaultValueByQuerySource(selectedItem: any, dataTree: any) {
    if (!isNil(selectedItem)) {
      const primaryField = !isEmpty(selectedItem.primaryFields) ? selectedItem.primaryFields[0] : null;

      this.joinDataSourceCategoryId = selectedItem.categoryId;
      this.foreignDataSourceCategoryId = selectedItem.categoryId;

      this.joinQuerySourceId = selectedItem.querySourceId;
      this.selectedForeignAlias = selectedItem.aliasId;

      this.joinQuerySourceName = selectedItem.querySourceName;
      this.foreignQuerySourceName = selectedItem.querySourceName;

      this.joinFieldId = !isEmpty(primaryField) ? primaryField.id : null;
      this.foreignFieldId = !isEmpty(primaryField) ? primaryField.id : null;

      // update CategoryName & FieldName
      const selectedCategory = find({ id: selectedItem.categoryId }, dataTree);
      if (selectedCategory && this.joinFieldId) {
        const selectedDataObject = find({ aliasId: selectedItem.aliasId }, selectedCategory.querySource);
        const selectedField = find({ id: this.joinFieldId }, selectedDataObject.fields);

        this.joinDataSourceCategoryName = selectedCategory.name;
        this.foreignDataSourceCategoryName = selectedCategory.name;
        this.joinFieldName = selectedField.name;
        this.foreignFieldName = selectedField.name;
      }
    }
  }

  /**
   * Add an exiting key join
   * @param {any} keyJoin
   * @param {number} index
   */
  addKeyJoin(keyJoin: any, index: number) {
    index ? this.relationshipKeyJoins.splice(index, 0, keyJoin) : this.relationshipKeyJoins.push(keyJoin);
  }

  /**
   * Add new key join
   * @param {boolean} isAllDataObjectUncategory
   */
  @action('addNewKeyJoin')
  addNewKeyJoin(isAllDataObjectUncategory: boolean) {
    let keyJoin = new ReportDesignerKeyJoinDetail();
    keyJoin.setParentId(this.tempId);

    if (isAllDataObjectUncategory) {
      keyJoin.setJoinDataSourceCategory(EMPTY_GUID);
      keyJoin.setForeignDataSourceCategory(EMPTY_GUID);
    }
    this.addKeyJoin(keyJoin);
    this.reindexKeyJoinsPosition();
  }

  /**
   * Copy a key join
   * @param {any} keyJoin
   */
  @action('copyKeyJoin')
  copyKeyJoin(keyJoin: any) {
    var newKeyJoin = keyJoin.clone();
    var index = findIndex({ tempId: keyJoin.tempId }, this.relationshipKeyJoins);
    this.addKeyJoin(newKeyJoin, index + 1);
    this.reindexKeyJoinsPosition();
  }

  /**
   * Set delete status is true.
   */
  delete() {
    this.state = OBJECT_STATE.DELETE;
  }

  /**
   * Set state
   * @param {number} state
   */
  setState(state: number) {
    this.state = state;
  }

  /**
   * Set value indicate this relationship has been modified with data model relationship
   * @param {boolean} value 
   */
  setModify(value: boolean) {
    this.hasBeenModified = value;
  }

  /**
   * Update state
   */
  @action('updateState')
  updateState() {
    // just update state to UPDATE when editing existing item
    this.id && this.setState(OBJECT_STATE.UPDATE);
    this.markDirty();
  }

  /**
   * Set alias text for the relationship.
   * @param {string} value
   */
  @action('setAlias')
  setAlias(value: string) {
    this.alias = value;
    this.updateState();
  }

  /**
   * Update current foreign alias when the source alias was changed
   * @param {string} joinQuerySourceId
   * @param {string} alias
   * @param {string} previousAlias
   */
  updateForeignAlias(joinQuerySourceId: string, alias: string, previousAlias: string) {
    if (this.selectedForeignAlias === `${joinQuerySourceId}_${previousAlias}`) {
      this.selectedForeignAlias = `${joinQuerySourceId}_${alias}`;
    }
  }

  /**
   * Update current foreign alias for key join when the source alias was changed
   * @param {string} joinQuerySourceId
   * @param {string} alias
   * @param {string} previousAlias
   */
  @action
  updateKeyJoinForeignAlias(joinQuerySourceId: string, alias: string, previousAlias: string) {
    forEach(keyJoin => {
      keyJoin.updateAlias(joinQuerySourceId, previousAlias, alias);
    }, this.relationshipKeyJoins);
  }

  /**
   * Update value for a property by its name
   * @param {string} key
   * @param {any} value
   * @param {boolean} notModifyState should modify state
   */
  @action('updateRelationshipItem')
  updateRelationshipItem(key: string, value: any, notModifyState: boolean) {
    this[key] = value;
    !notModifyState && this.updateState();
  }

  /**
   * Get id value.
   */
  getId() {
    return this.id || this.tempId;
  }

  /**
   * Populate new id value.
   */
  @action('newId')
  newId() {
    this.id = null;
    this.tempId = this.validationKey = uniqueId('relationship_');
  }

  /**
   * Update relationship's position
   * @param {number} position
   */
  setRelationshipPosition(position: number) {
    this.relationshipPosition = position;
  }

  /**
   * Update hidden status
   * @param {any} hidden
   */
  setHidden(hidden: any) {
    this.hidden = hidden;
  }

  /**
   * clone a totally a new relationship including keyJoins
   */
  @action('cloneRelationship')
  cloneRelationship() {
    let relationshipJson = toJS(this);
    let cloneObj = cloneDeep(relationshipJson);
    let keyJoins = cloneDeep(toJS(relationshipJson.relationshipKeyJoins));

    cloneObj.relationshipKeyJoins = [];
    placeholder(keyJoins).forEach(item => {
      cloneObj.relationshipKeyJoins.push(new ReportDesignerKeyJoinDetail(item));
    });

    return cloneObj;
  }

  /**
   * Delete a key join
   * @param {any} keyJoin
   */
  deleteKeyJoin(keyJoin: any) {
    isEmpty(keyJoin.id)
      ? this.relationshipKeyJoins.splice(this.relationshipKeyJoins.indexOf(keyJoin), 1)
      : keyJoin.delete();
  }

  /**
   * Reindex Alias Level & Relationship Position of DataSourceGrid.
   * Then update Alias Level from new row -> end
   */
  @action('reindexKeyJoinsPosition')
  reindexKeyJoinsPosition() {
    function updatePositionAndLevel(level) {
      return action((item, index) => {
        item.position = index;
        item.level = level;
      });
    }

    const updatePositionAndLevelProcess = updatePositionAndLevel(this.level);

    this.relationshipKeyJoins.forEach(updatePositionAndLevelProcess);
  }

  /**
   * Update Join Type : Clear, Disable Field & Join Field when value = 'Cross'
   * @param {string} key
   * @param {any} value
   */
  @action('updateJoinType')
  updateJoinType(key: string, value: any) {
    this[key] = value;
    this.joinType === JOIN_TYPE_CROSS && (this.relationshipKeyJoins = []);
    this.updateState();
  }

  /**
   * Clear key joins : Clear all key joins when user select 'Cross'
   */
  @action('clearKeyJoin')
  clearKeyJoin() {
    this.relationshipKeyJoins = [];
    this.updateState();
  }

  /**
   * create data for copying relationship
   */
  @action('createDataForCopying')
  createDataForCopying() {
    this.level++;
    this.newId();
    this.setState(OBJECT_STATE.INSERT);
    this.generateAliasTempId();
  }

  /**
   * Set level for relationship
   * @param {number} level
   */
  setLevel(level: number) {
    this.level = level;
  }

  /**
   * generate aliasTempId
   */
  generateAliasTempId() {
    this.aliasTempId = uniqueId('alias_');
  }

  /**
   * Set Comparison Operator
   * @param {any} value
   */
  @action('setComparisionOperator')
  setComparisionOperator(value: any) {
    this.comparisonOperator = value;
    this.updateState();
  }

  /**
   * Set Comparison Value
   * @param {any} value
   */
  @action('setComparisionValue')
  setComparisionValue(value: any) {
    this.comparisonValue = value;
    this.updateState();
  }
}

export default ReportDesignerRelationshipDetail;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerRelationshipDetail.js